import { AxiosInstance } from "axios";
import LoadingState from "./LoadingState";

/** @type {Object} */
const defaults = {
  /** @type {Number[]} */
  statusCodes: [
    401, // Unauthorized
  ],
};

/**
 * Determines if request should launch auth workflow or reject immediately.
 *
 * @param {boolean} needsCredentialRefresh
 * @param {*} error
 * @param {number[]} statusCodes
 * @returns {boolean}
 */
const shouldRejectRequest = (error: any, statusCodes: number[]): boolean => {
  return (
    !error.response ||
    (error.response.status && !statusCodes.includes(+error.response.status))
  );
};

/**
 * Creates an authentication refresh interceptor that binds to any error response.
 * If the response code is 401, interceptor tries to call the refreshTokenCall which must return a Promise.
 * While refreshTokenCall is running, all new requests are intercepted and waiting for it to resolve.
 * After Promise is resolved/rejected the authentication refresh interceptor is revoked.
 * @param {AxiosInstance} axios - axios instance
 * @param {Function} refreshTokenCall - refresh token call which must return a Promise
 * @param {Object} options - options for the interceptor @see defaultOptions
 * @return {AxiosInstance}
 */
function authFlowInterceptorFactory(
  axios: AxiosInstance,
  refreshTokenCall: (...errors: any[]) => Promise<LoadingState>,
  options = defaults
): AxiosInstance {
  const id = axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (!error) {
        return Promise.reject(error);
      }

      // Reject promise if the error status is not in options.ports or defaults.ports
      const statusCodes =
        options.statusCodes && options.statusCodes.length > 0
          ? options.statusCodes
          : defaults.statusCodes;
      if (shouldRejectRequest(error, statusCodes)) {
        return Promise.reject(error);
      }

      // Remove the interceptor to prevent a loop
      // in case token refresh also causes the 401
      axios.interceptors.response.eject(id);

      console.log("Caught auth error in interceptor");

      const refreshCall = refreshTokenCall(error);

      // Create interceptor that will bind all the others requests
      // until refreshTokenCall is resolved
      const requestQueueInterceptorId = axios.interceptors.request.use(
        (request) => refreshCall.then(() => request)
      );

      // When response code is 401 (Unauthorized), try to refresh the token.
      return refreshCall
        .then(() => {
          axios.interceptors.request.eject(requestQueueInterceptorId);
          return axios(error.config);
        })
        .catch((error2) => {
          axios.interceptors.request.eject(requestQueueInterceptorId);
          return Promise.reject(error2);
        })
        .finally(() =>
          authFlowInterceptorFactory(axios, refreshTokenCall, options)
        );
    }
  );
  return axios;
}
export default authFlowInterceptorFactory;
